import { ImMessage } from "@whatslly/types/tuvis-messaging/TuvisMessagingMessages";
import { ImChat } from "@whatslly/types/tuvis-messaging/TuvisMessagingChats";
import { ImConversation, ConversationStatus, ConversationManagementOptions } from "@whatslly/types/tuvis-messaging/TuvisMessagingConversations";
import { ImContact } from "@whatslly/types/tuvis-messaging/TuvisMessagingContacts";
import { AssignableAgent } from "@whatslly/types/tuvis-messaging/TuvisMessagingConversations";
import { StringifiedMutation, MessagingRepo } from "@whatslly/types/tuvis-messaging/TuvisMessagingStream";
import { TemplateLanguages } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";
import { NotificationSubscription } from "@whatslly/server-commons/database/entities/users.entity";
import { OptInTemplate, HeaderMedia } from "@whatslly/types/waba/WhatsappBusinessApiSelectChat";
import { ImLabel } from "@whatslly/server-commons/tuvis-messaging/entities/im-labels.entity";
import { TuvisMessagingPhone } from "@whatslly/types/tuvis-messaging/TuvisMessagingPhones";
import type { SelectedPhone } from "@whatslly/types/tuvis-messaging/TuvisMessagingPhones";
import {
  CreateImCampaignData,
  GetImCampaignItemsParams,
  GetImCampaignItemsResponse,
  GetImCampaignsParams,
  GetImCampaignsResponse,
} from "@whatslly/types/tuvis-messaging/TuvisMessagingCampaign";

export const MESSAGING_COMMANDS_PREFIX = "tuvis-messaging";
export type ContactToImportFromCsvType = { firstName: string; lastName: string; phone: string };

export const MessagingCommands = {
  ABORT_TRANSACTION: `${MESSAGING_COMMANDS_PREFIX}:abort-transaction`,
  RESPONSE: `${MESSAGING_COMMANDS_PREFIX}:web-script-response`,
  ERROR: `${MESSAGING_COMMANDS_PREFIX}:web-script-error`,
  PING: `${MESSAGING_COMMANDS_PREFIX}:ping`,
  GET_USER_PHONES: `${MESSAGING_COMMANDS_PREFIX}:get-user-phones`,
  GET_TEMPLATES: `${MESSAGING_COMMANDS_PREFIX}:get-templates`,
  GET_INIT_DATA: `${MESSAGING_COMMANDS_PREFIX}:get-init-data`,
  REGISTER_NOTIFICATIONS_ENDPOINT: `${MESSAGING_COMMANDS_PREFIX}:register-notifications-endpoint`,
  UNREGISTER_NOTIFICATIONS_ENDPOINT: `${MESSAGING_COMMANDS_PREFIX}:unregister-notifications-endpoint`,
  CREATE_CONTACT: `${MESSAGING_COMMANDS_PREFIX}:create-contact`,
  UPDATE_CONTACT: `${MESSAGING_COMMANDS_PREFIX}:update-contact`,
  DELETE_CONTACT: `${MESSAGING_COMMANDS_PREFIX}:delete-contact`,
  CREATE_CHAT: `${MESSAGING_COMMANDS_PREFIX}:create-chat`,
  IMPORT_CONTACTS_FROM_CSV: `${MESSAGING_COMMANDS_PREFIX}:import-chats-from-csv`,
  ARCHIVE_CHAT: `${MESSAGING_COMMANDS_PREFIX}:archive-chat`,
  UNARCHIVE_CHAT: `${MESSAGING_COMMANDS_PREFIX}:unarchive-chat`,
  ADD_CHAT_TO_CONTACT: `${MESSAGING_COMMANDS_PREFIX}:add-chat-to-contact`,
  SELECT_CHAT: `${MESSAGING_COMMANDS_PREFIX}:select-chat`,
  SEND_TEMPLATE_TO_SELECTED_CHATS: `${MESSAGING_COMMANDS_PREFIX}:send-templates-to-selected-chats`,
  MARKE_MESSAGE_AS_READ: `${MESSAGING_COMMANDS_PREFIX}:mark-message-as-read`,
  SEND_TEXT_MESSAGE: `${MESSAGING_COMMANDS_PREFIX}:send-text-message`,
  SEND_MEDIA_MESSAGE: `${MESSAGING_COMMANDS_PREFIX}:send-media-message`,
  SEND_LOCATION_MESSAGE: `${MESSAGING_COMMANDS_PREFIX}:send-location-message`,
  SEND_MESSAGE_REACTION: `${MESSAGING_COMMANDS_PREFIX}:send-message-reaction`,
  RE_SEND_MESSAGE: `${MESSAGING_COMMANDS_PREFIX}:re-send-message`,
  MODIFY_LABEL_TO_SELECTED_CHATS: `${MESSAGING_COMMANDS_PREFIX}:modify-label-to-selected-chats`,
  CREATE_LABEL: `${MESSAGING_COMMANDS_PREFIX}:create-label`,
  DELETE_LABEL: `${MESSAGING_COMMANDS_PREFIX}:delete-label`,
  CREATE_IM_CAMPAIGN: `${MESSAGING_COMMANDS_PREFIX}:create-im-campaign`,
  GET_IM_CAMPAIGNS: `${MESSAGING_COMMANDS_PREFIX}:get-im-campaigns`,
  GET_IM_CAMPAIGN_ITEMS: `${MESSAGING_COMMANDS_PREFIX}:get-im-campaign-items`,
  UPDATE_CONVERSATION_STATUS: `${MESSAGING_COMMANDS_PREFIX}:update-conversation-status`,
  ASSIGN_AGENTS_TO_CONVERSATION: `${MESSAGING_COMMANDS_PREFIX}:assign-agents-to-conversation`,
  GET_ASSIGNABLE_AGENTS: `${MESSAGING_COMMANDS_PREFIX}:get-assignable-agents`,
  GET_CHAT_CONVERSATIONS: `${MESSAGING_COMMANDS_PREFIX}:get-chat-conversations`,
} as const;

export type MessagingCommandsKeys = keyof typeof MessagingCommands;

export type MessagingCommandsTypes = {
  [MessagingCommands.ABORT_TRANSACTION]: never;
  [MessagingCommands.RESPONSE]: never;
  [MessagingCommands.ERROR]: never;
  [MessagingCommands.PING]: () => "pong";
  [MessagingCommands.GET_USER_PHONES]: () => { phones: Array<TuvisMessagingPhone> };
  [MessagingCommands.GET_TEMPLATES]: () => { templates: TemplateLanguages[] };
  [MessagingCommands.GET_INIT_DATA]: (params: { lastMsgId: string | null }) => {
    contacts: ImContact[];
    chats: ImChat[];
    messages: ImMessage[];
    labels: ImLabel[];
    conversations: ImConversation[];
    delta: Array<StringifiedMutation<MessagingRepo> & { msgId: string }>;
  };
  [MessagingCommands.REGISTER_NOTIFICATIONS_ENDPOINT]: (params: NotificationSubscription) => void;
  [MessagingCommands.UNREGISTER_NOTIFICATIONS_ENDPOINT]: (params: Pick<NotificationSubscription, "endpoint" | "type">) => void;
  [MessagingCommands.CREATE_CONTACT]: (params: { phone: string; firstName: string; lastName: string }) => { _id: string };
  [MessagingCommands.UPDATE_CONTACT]: (params: { _id: string; firstName: string; lastName: string }) => void;
  [MessagingCommands.DELETE_CONTACT]: (params: { _id: string }) => void;
  [MessagingCommands.CREATE_CHAT]: (params: { contactId: string; phone: number; phoneId: string; type: ImChat["type"] }) => { chat: ImChat };
  [MessagingCommands.ARCHIVE_CHAT]: (params: { _id: string }) => void;
  [MessagingCommands.UNARCHIVE_CHAT]: (params: { _id: string }) => void;
  [MessagingCommands.ADD_CHAT_TO_CONTACT]: (params: {
    chatId: string;
    phone: number;
    contactId?: string;
    firstName?: string;
    lastName?: string;
  }) => void;
  [MessagingCommands.SELECT_CHAT]: (
    params: {
      selectedPhone: SelectedPhone;
      _id: string;
      optInTemplate?: OptInTemplate;
      headerMedia?: HeaderMedia;
    } & ConversationManagementOptions
  ) => void;
  [MessagingCommands.SEND_TEMPLATE_TO_SELECTED_CHATS]: (
    params: {
      chats: { selectedPhone: SelectedPhone; _id: string }[];
      optInTemplate?: OptInTemplate;
      headerMedia?: HeaderMedia;
    } & ConversationManagementOptions
  ) => { chatName: string; success: boolean }[];
  [MessagingCommands.MARKE_MESSAGE_AS_READ]: (params: { selectedPhone: SelectedPhone; msgIds: string[] }) => void;
  [MessagingCommands.SEND_TEXT_MESSAGE]: (params: {
    selectedPhone: SelectedPhone;
    text: string;
    recipient: number;
    replyMessageId?: string;
    chatId?: string;
    conversationId?: string;
  }) => void;
  [MessagingCommands.SEND_MEDIA_MESSAGE]: (params: {
    selectedPhone: SelectedPhone;
    text: string | undefined;
    recipient: number;
    replyMessageId?: string;
    chatId?: string;
    conversationId?: string;
    binary: string;
    binaryType?: "binary" | "base64";
    mimeType: string;
    width?: number;
    height?: number;
    fileName: string;
  }) => void;
  [MessagingCommands.SEND_LOCATION_MESSAGE]: (params: {
    selectedPhone: SelectedPhone;
    location: { latitude: number; longitude: number };
    recipient: number;
    replyMessageId?: string;
    chatId?: string;
    conversationId?: string;
  }) => void;
  [MessagingCommands.SEND_MESSAGE_REACTION]: (params: { selectedPhone: SelectedPhone; recipient: number; msgId: string; emoji: string }) => void;
  [MessagingCommands.RE_SEND_MESSAGE]: (params: {
    selectedPhone: SelectedPhone;
    msgId: string;
  }) => { success: true; msgId: string } | { success: false; msgId: string; error: string };
  [MessagingCommands.IMPORT_CONTACTS_FROM_CSV]: (contacts: ContactToImportFromCsvType[]) => { success: boolean };
  [MessagingCommands.MODIFY_LABEL_TO_SELECTED_CHATS]: (params: { chatsIds: string[]; labelsToAdd: string[]; labelsToRemove: string[] }) => boolean;
  [MessagingCommands.CREATE_LABEL]: (params: { labelName: string }) => { imLabel: ImLabel };
  [MessagingCommands.DELETE_LABEL]: (params: { _id: string }) => { success: boolean };
  [MessagingCommands.CREATE_IM_CAMPAIGN]: (params: CreateImCampaignData) => { campaignId: string };
  [MessagingCommands.GET_IM_CAMPAIGNS]: (params: GetImCampaignsParams) => GetImCampaignsResponse;
  [MessagingCommands.GET_IM_CAMPAIGN_ITEMS]: (params: GetImCampaignItemsParams) => GetImCampaignItemsResponse;
  [MessagingCommands.UPDATE_CONVERSATION_STATUS]: (params: { chatId: string; status: ConversationStatus }) => {
    success: boolean;
    conversation?: ImConversation;
  };
  [MessagingCommands.ASSIGN_AGENTS_TO_CONVERSATION]: (params: { chatId: string; addAgentIds: string[]; removeAgentIds: string[] }) => {
    success: boolean;
    conversation?: ImConversation;
  };
  [MessagingCommands.GET_ASSIGNABLE_AGENTS]: (params: { phoneId?: string }) => { agents: AssignableAgent[] };
  [MessagingCommands.GET_CHAT_CONVERSATIONS]: (params: { chatId: string; limit?: number }) => { conversations: ImConversation[] };
};
